import * as THREE from "three";
import { VFX } from "../../../../../art/vfx.js";

/**
 * Fortress damage states (ART_WOW_PASS Phase 3 §3, art_review/towers.md F6e), driven by the base's hp.
 *
 * The P3 fortress GLBs (base_t1..3) carry both damage stages as hidden parts — node visibility, not extra models, so a
 * hit costs no download, no swap and no new draw call (the parts share the fortress's two materials and are bones of
 * its rigid rig, see rigidRig.ts):
 *  - `dmg1_rubble*` (rest scale 0) and `dmg1_smoke*` (empty anchors) — shown at ≤ 66 % hp; the banners (`flag*`) come
 *    down at the same stage;
 *  - `dmg2_fire*` (empty anchors on the corner-tower roofs, where the flame sprites burn) and `dmg2_rubble*` — ≤ 33 %.
 * The rubble heaps were re-authored (p3out/rubble, 2026-09-22): clones of the fortress's own wall blocks, merlons and
 * charred beams on the `_tex` material, so they share its atlas and texel density instead of the old grey slabs.
 * Smoke columns rise from the `dmg1_smoke*` anchors and embers from the fires through the pooled VFX (ambient class).
 * Models without these parts (the Dragonlord skin, older bakes) are left untouched.
 *
 * Call `update` every frame AFTER the rig update: the idle clip animates the banners' scale, so hiding them is a
 * per-frame write, and so is the fires' flicker.
 */
export class FortressDamage {
  /** hp fractions at which stage 1 and stage 2 begin (inclusive). */
  static readonly Stage1 = 2 / 3;
  static readonly Stage2 = 1 / 3;
  /** Seconds between smoke puffs per anchor, and between embers per fire. */
  static readonly SmokeEvery = 0.3;
  static readonly EmberEvery = 0.35;
  /** Roof-blaze sprites: one per fire anchor every `FlameEvery` s, `FlameSize` world units (a corner turret is ~0.6 since fortress v2). */
  static readonly FlameEvery = 0.09;
  static readonly FlameSize = 0.42;
  /** Show the baked rubble parts (the re-authored heaps; `false` falls back to the VFX-only damage). */
  static MeshParts = true;
  private static readonly SmokeColour: [number, number, number] = [0.22, 0.2, 0.2];

  private readonly stage1: THREE.Object3D[] = [];
  private readonly stage2: THREE.Object3D[] = [];
  private readonly smoke: THREE.Object3D[] = [];
  private readonly fires: THREE.Object3D[] = [];
  private readonly flags: THREE.Object3D[] = [];
  private smokeIn = 0;
  private emberIn = 0;
  private flameIn = 0;
  private t = 0;
  private readonly tmp = new THREE.Vector3();

  private constructor(root: THREE.Object3D, name: string) {
    root.traverse((o) => {
      switch (FortressDamage.roleOf(o.name, name)) {
        case "smoke": this.smoke.push(o); break;
        case "stage1": this.stage1.push(o); break;
        case "fire": this.fires.push(o); this.stage2.push(o); break;
        case "stage2": this.stage2.push(o); break;
        case "flag": this.flags.push(o); break;
      }
    });
  }

  /**
   * What a node of the fortress is to the damage rig, by name. Only the named parts themselves (`dmg1_rubble0`,
   * `dmg2_fire3`, `flag0`) match, never the nodes under them: GLTFLoader names a part's unnamed child after it
   * (`dmg1_rubble0_1`), and that child carries the mesh's dequantisation scale (about 0.25 for a quantized heap).
   * Matching it too (the old `dmg1_\\w+`) set that scale to 1 at stage 1, and every heap drew three to four times its
   * size: a 2 m slab of stretched stone texture standing in the courtyard (owner report 2026-09-24).
   */
  static roleOf(node: string, name: string): "smoke" | "stage1" | "fire" | "stage2" | "flag" | null {
    const m = new RegExp(`^${name}\\.?(dmg[12]_[a-z]+|flag)(\\d+)$`).exec(node);
    if (!m) return null;
    const part = m[1];
    if (part === "flag") return "flag";
    if (part === "dmg1_smoke") return "smoke";
    if (part === "dmg2_fire") return "fire";
    return part.startsWith("dmg1_") ? "stage1" : "stage2";
  }

  /** The damage rig of a fortress model, or null when the model has no damage parts. */
  static of(root: THREE.Object3D, name: string): FortressDamage | null {
    if (!name) return null;
    const d = new FortressDamage(root, name);
    return d.stage1.length + d.stage2.length > 0 ? d : null;
  }

  /** 0 healthy, 1 at ≤ 66 % hp, 2 at ≤ 33 %. */
  static stageOf(hp: number, max: number): 0 | 1 | 2 {
    const f = max > 0 ? hp / max : 1;
    return f <= FortressDamage.Stage2 ? 2 : f <= FortressDamage.Stage1 ? 1 : 0;
  }

  update(stage: 0 | 1 | 2, dt: number, fx: VFX | null): void {
    this.t += dt;
    // The rubble heaps (`dmg*_rubble*`) scale in at their stage. They used to be untextured grey slabs and the fires
    // pale cones, so both were hidden; the heaps are now re-authored from the fortress's own stone, and the
    // `dmg2_fire*` nodes are empty anchors (the flame sprites below burn there), so they are never scaled up.
    // `MeshParts = false` keeps every damage node an anchor only.
    const parts = FortressDamage.MeshParts;
    for (const o of this.stage1) o.scale.setScalar(parts && stage >= 1 ? 1 : 0);
    for (const o of this.stage2) o.scale.setScalar(parts && stage >= 2 && !this.fires.includes(o) ? 1 : 0);
    if (stage >= 1) for (const o of this.flags) o.scale.setScalar(0);
    if (!fx || stage === 0) return;
    this.smokeIn -= dt;
    if (this.smokeIn <= 0) {
      this.smokeIn = FortressDamage.SmokeEvery;
      for (const o of this.smoke) fx.smoke(o.getWorldPosition(this.tmp), 1, { size: 0.5 + 0.2 * stage, life: 2.2, rise: 1.4, spread: 0.25, color: FortressDamage.SmokeColour, alpha: 0.55 });
    }
    if (stage < 2) return;
    this.flameIn -= dt;
    if (this.flameIn <= 0) {
      this.flameIn = FortressDamage.FlameEvery;
      this.fires.forEach((o, i) => {
        const k = 1 + 0.2 * Math.sin(this.t * 9 + i * 1.7);
        fx.flames(o.getWorldPosition(this.tmp), { size: FortressDamage.FlameSize * k, life: 0.5 });
      });
    }
    this.emberIn -= dt;
    if (this.emberIn <= 0 && this.fires.length > 0) {
      this.emberIn = FortressDamage.EmberEvery;
      const o = this.fires[Math.floor(Math.random() * this.fires.length)];
      fx.embers(o.getWorldPosition(this.tmp).setY(this.tmp.y + 0.15), 3, { radius: 0.15, life: 1.2 });
    }
  }
}
